import React, { useState, useRef, useEffect } from 'react';
import init, { predict } from 'brique-demo-wasm';
import './App.css';
import {Button} from '@mantine/core';
  
// function SquaredGrid({n, data}: {n: number, data: number[]}) {
//   const elements = Array.from({length: n}, (_, index) => (
//     <div key={index} className='box' style={{opacity: data[index]/255}}>{index}</div>
//   ));
//   return <div className='grid'>{elements}</div>
// } 

interface DrawingCanvasProps {
  width: number;
  height: number;
  penWidth: number;
  setProb: (newProb: Float64Array) => void;
}

export default function DrawingCanvas(props : DrawingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const initialPixelMap = new Array(28*28).fill(0);
  const [drawing, setDrawing] = useState<boolean>(false);

  const [pixelMap, setPixelMap] = useState<number[]>(initialPixelMap);
  const [lastPos, setLastPos] = useState<{x: number, y: number} | null>(null);
  const [model, setModel] = useState<Uint8Array>();

  useEffect(() => {
    let fetchModel = async() => { 
      const model = await processModel()
      setModel(model);
      console.log("model has been set")
    };
    
    fetchModel()
  }, []);

  function startDrawing(e: React.MouseEvent<HTMLCanvasElement>) {
    setDrawing(true);
    setLastPos(null);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
  }

  function draw(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const currentX = e.nativeEvent.offsetX;
    const currentY = e.nativeEvent.offsetY;
    
    // If this is the first point in this stroke, initialize lastPos
    if (lastPos === null) {
      setLastPos({ x: currentX, y: currentY });
      return;
    }
    
    // Create a gradient along the line
    const gradient = ctx.createLinearGradient(
      lastPos.x, lastPos.y,
      currentX, currentY
    );

    // Define gradient stops for fade effect
    gradient.addColorStop(0, `rgba(0, 0, 0, 0.5)`); // Start with partial opacity
    gradient.addColorStop(0.2, `rgba(0, 0, 0, 1)`); // Full opacity in the middle
    gradient.addColorStop(0.8, `rgba(0, 0, 0, 1)`); // Full opacity in the middle
    gradient.addColorStop(1, `rgba(0, 0, 0, 0.5)`); // End with partial opacity

    // Set the stroke style to use our gradient
    ctx.strokeStyle = gradient;
    ctx.lineWidth = props.penWidth;
    ctx.lineCap = "round";

    // Draw the line segment
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(currentX, currentY);
    ctx.stroke();

    // Update last position for the next segment
    setLastPos({ x: currentX, y: currentY });
    getPixelMap(); 
  }

  function stopDrawing() {
    setDrawing(false);
  }

  function clear() {
    const canvas = canvasRef.current;
    if(!canvas) return;
    const ctx = canvas.getContext("2d");
    if(!ctx) return;
    ctx.clearRect(0,0, canvas.width, canvas.height);
    getPixelMap();
  }

  function getPixelMap() {
    const canvas = canvasRef.current;
    if (!canvas) return [];
    const ctx = canvas.getContext("2d");
    if (!ctx) return [];
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let rawPixelMap: number[] = [];
    
    for (let i = 3; i < imageData.data.length; i += 4) {
       rawPixelMap.push(imageData.data[i]);
    }

    let newPixelMap = resizeBitmap(rawPixelMap, canvas.width, canvas.height);
    newPixelMap = centerBitmap(newPixelMap);
    setPixelMap(newPixelMap);
  }

  
  function resizeBitmap(
    src: number[], 
    srcWidth: number, 
    srcHeight: number, 
    targetWidth: number = 28, 
    targetHeight: number = 28
  ): number[] {
    const dst = new Array(targetWidth * targetHeight).fill(0);
    const scaleX = srcWidth / targetWidth;
    const scaleY = srcHeight / targetHeight;
  
    for (let y = 0; y < targetHeight; y++) {
      for (let x = 0; x < targetWidth; x++) {
        // Map target pixel to source position
        const srcX = x * scaleX;
        const srcY = y * scaleY;
        
        // Get integer coordinates
        const x0 = Math.floor(srcX);
        const x1 = Math.min(x0 + 1, srcWidth - 1);
        const y0 = Math.floor(srcY);
        const y1 = Math.min(y0 + 1, srcHeight - 1);
  
        // Get fractional parts
        const dx = srcX - x0;
        const dy = srcY - y0;
  
        // Get pixel values
        const p00 = src[y0 * srcWidth + x0];
        const p10 = src[y0 * srcWidth + x1];
        const p01 = src[y1 * srcWidth + x0];
        const p11 = src[y1 * srcWidth + x1];
  
        // Bilinear interpolation
        const interpolated =
          p00 * (1 - dx) * (1 - dy) +
          p10 * dx * (1 - dy) +
          p01 * (1 - dx) * dy +
          p11 * dx * dy;
  
        dst[y * targetWidth + x] = Math.round(interpolated);
      }
    }
  
    return dst;
  }

  function centerBitmap(bitmap: number[], width: number = 28, height: number = 28): number[] {
    // Create a new empty bitmap
    const result = Array(width * height).fill(0);
    
    // Find the bounds of the content
    let minX = width;
    let maxX = -1;
    let minY = height;
    let maxY = -1;
    
    // Scan the bitmap to find the content boundaries
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = y * width + x;
        if (bitmap[index] > 0) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
    }
    
    // Handle empty bitmap case
    if (minX > maxX || minY > maxY) {
      return result;
    }
    
    // Calculate content dimensions
    const contentWidth = maxX - minX + 1;
    const contentHeight = maxY - minY + 1;
    
    // Calculate offsets to center the content
    const offsetX = Math.floor((width - contentWidth) / 2);
    const offsetY = Math.floor((height - contentHeight) / 2);
    
    // Copy the content to the centered position
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const sourceIndex = y * width + x;
        const targetX = x - minX + offsetX;
        const targetY = y - minY + offsetY;
        const targetIndex = targetY * width + targetX;
        
        result[targetIndex] = bitmap[sourceIndex];
      }
    }
    
    return result;
  }

  function readBinaryFile(blob: Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.result instanceof ArrayBuffer) {
                resolve(new Uint8Array(reader.result));
            } else {
                reject(new Error("Failed to read file as ArrayBuffer"));
            }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
  }

  async function processModel(): Promise<Uint8Array> {
      const response = await fetch("mnist_128x128_0.9775.brq"); 
      const blob = await response.blob(); 
  
      const byteArray = await readBinaryFile(blob);
      return byteArray;
  }

  async function modelEvaluation() {
    await init();
     let image_input = Float64Array.from(pixelMap);
     if(model) {
      let model_output = predict(model, image_input);
      props.setProb(model_output);
     }
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={props.width}
        height={props.height}
        style={{ border: "1px solid black", background: "white" }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseOut={stopDrawing}
      />
      <div className="btns">
        <Button onClick={clear}>Clear</Button>
        <Button onClick={modelEvaluation}>Predict</Button>
      </div>
    </div>
  );
}
